import React, { useEffect, useState } from 'react';
import {
	View,
	StyleSheet,
	Dimensions,
	TouchableOpacity,
	Pressable,
	Text,
	ActivityIndicator,
} from 'react-native';
import { createGlobalStyle } from 'styled-components';
import TableContainer from '../../../../components/web/ReactTable/Table.container';
import I18n from '../../../../components/shared/i18n/I18n.component';
import colors from '../../../../config/colors';
import httpService from '../../../../services/http.service';
import { i18nService } from '../../../../services/i18n.service';

interface Props {
	businessUnitsList: any[];
	onClose(): void;
}

const windowHeight = Dimensions.get('window').height;

const TableStyle = createGlobalStyle`
  .roles-permissions-table thead th {
    background-color: #282C6F !important;
    color: white !important;
  }
  .roles-permissions-table th,
  .roles-permissions-table td {
    border-color: #E2EDF4 !important;
  }
  .roles-permissions-table thead th:not(:last-child) {
    border-left: 1px solid #5B5D85 !important;
  }
  .roles-permissions-table tbody td:not(:last-child) {
    border-left: 1px solid #E2EDF4 !important;
  }
  .roles-permissions-table tbody td {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    line-height: 20px !important;
  }
  .roles-permissions-table * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .roles-permissions-table *::-webkit-scrollbar {
    display: none;
  }
`;

const permissionHeaders = [
	{
		accessor: 'category_name',
		Header: i18nService.translate(
			'usersManagement.rolesAndPermissions.permissionsTable.category_name',
		),
		isSortable: false,
		style: { width: 160 },
		CustomCell: ({ row }: any) => (
				<I18n size={14} font='medium' color='primary'>{row.original.category_name}</I18n>
			),
	},
	{
		accessor: 'name',
		Header: i18nService.translate(
			'usersManagement.rolesAndPermissions.permissionsTable.name',
		),
		isSortable: false,
		style: { width: 180 },
	},
	{
		accessor: 'description',
		Header: i18nService.translate(
			'usersManagement.rolesAndPermissions.permissionsTable.description',
		),
		isSortable: false,
	},
];

// groups the permissions by category - the category name is shown once, on the
// first row of the group, and left empty on the rest of its rows
const groupByCategory = (permissions: any[]) => {
	const sorted = [...permissions].sort((a: any, b: any) =>
		a.category_id === b.category_id ? 0 : a.category_id > b.category_id ? 1 : -1,
);

	return sorted.map((permission: any, index: number) => ({
		...permission,
		category_name:
			sorted[index - 1]?.category_name === permission.category_name
				? ''
				: permission.category_name,
	}));
};

const RolesAndPermissionsModal = ({ businessUnitsList, onClose }: Props) => {
	const [roles, setRoles] = useState<any[]>([]);
	const [activeIndex, setActiveIndex] = useState(0);
	const [permissionsByRole, setPermissionsByRole] = useState<{
		[roleId: number]: any[];
	}>({});
	const [loading, setLoading] = useState(true);

	const unitIds = businessUnitsList.map((unit: any) => unit.id).join(',');

	const loadPermissions = async (roleId: number) => {
		if (permissionsByRole[roleId]) {
			return;
		}
		try {
			const res = await httpService.api<any>({
				type: 'getRolePermissions',
				params: { id: roleId },
				query: { unitId: unitIds },
				disableBI: true,
			});
			if (res) {
				setPermissionsByRole(prev => ({
					...prev,
					[roleId]: groupByCategory(res),
				}));
			}
		} catch (e) {}
	};

	useEffect(() => {
		if (!businessUnitsList.length) {
			setLoading(false);
			return;
		}

		async function fetchRoles() {
			try {
				const res = await httpService.api<any>({
					type: 'getRoles',
					query: { unitIds },
					disableBI: true,
				});
				if (res?.length) {
					setRoles(res);
					await loadPermissions(res[0].id);
				}
			} catch (e) {
				console.error(e);
			} finally {
				setLoading(false);
			}
		}

		fetchRoles();

	}, [businessUnitsList]);

	const onSwitchTab = (index: number) => {
		setActiveIndex(index);
		loadPermissions(roles[index].id);
	};

	return (
		<View style={styles.container}>
			<TableStyle />
			<View style={styles.header}>
				<I18n size={20} weight='bold' color='primaryDarkBlue'>
					usersManagement.rolesAndPermissions.modalTitle
				</I18n>
				<Pressable onPress={onClose} style={styles.closeButton}>
					<Text style={styles.closeButtonText}>×</Text>
				</Pressable>
			</View>

			<View style={styles.tabsContainer}>
				{roles.map((role: any, index: number) => (
					<TouchableOpacity
						key={role.id}
						onPress={() => onSwitchTab(index)}
						style={[
							styles.buttonTab,
							{
								borderBottomColor:
									activeIndex === index
										? colors.titlePrimary
										: colors.white,
							},
						]}
					>
						<I18n size={16} color='titlePrimary' font='medium'>
							{role.name}
						</I18n>
					</TouchableOpacity>
				))}
			</View>

			{loading ? (
				<View style={styles.loaderContainer}>
					<ActivityIndicator color={colors.primary} size='large' />
				</View>
			) : (
				<div className='roles-permissions-table' style={tableStyle}>
					<TableContainer
						keyName={''}
						headers={permissionHeaders as any}
						data={permissionsByRole[roles[activeIndex]?.id] || []}
						getNextData={() => {}}
						allDataFetched
						noInfinityScroll
						hasFooter={false}
						onSort={() => {}}
						getUpdatedDataFromTable={() => {}}
						windowScale={0}
					/>
				</div>
			)}
		</View>
	);
};

const tableStyle = {
	border: '1px solid #E2EDF4',
	borderRadius: 8,
	overflow: 'hidden' as const,
	marginTop: 20,
};

const styles = StyleSheet.create({
	container: {
		backgroundColor: colors.white,
		borderRadius: 20,
		paddingVertical: 30,
		paddingHorizontal: 20,
		width: '60vw',
		height: windowHeight - 200,
	},
	header: {
		flexDirection: 'row-reverse',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBottom: 5,
	},
	tabsContainer: {
		flexDirection: 'row-reverse',
		borderBottomWidth: 1,
		borderBottomColor: '#e7e7e8',
	},
	loaderContainer: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: 20,
	},
	buttonTab: {
		width: 150,
		borderBottomWidth: 2,
		alignItems: 'center',
		padding: 3,
		marginTop: 20,
	},
	closeButton: {
		padding: 4,
		marginLeft: 8,
	},
	closeButtonText: {
		fontSize: 28,
		color: colors.darkGrey,
		lineHeight: 28,
	},
});

export default RolesAndPermissionsModal;
